// Plans: what each subscription tier includes. Companies get their plan's
// features plus per-company exceptions (set on the Company Features page).
// Saving here recalculates every company on a changed tier; the server keeps
// their exceptions and their admins' own switches.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  PLAN_MIGRATION,
  PLAN_MIGRATION_ROUTE,
  TIERS,
  getPlans,
  groupFeatures,
  isCore,
  isPlanSchemaMissing,
  savePlans,
  tierName,
} from '../api/features'
import type { FeatureDefinition, PlanChange, PlansResponse, Tier } from '../api/features'

const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback

type PlanSets = Record<string, Set<string>>

const toSets = (plan: Record<string, string[]>): PlanSets =>
  Object.fromEntries(TIERS.map((t) => [t, new Set(plan[t] || [])]))

export function PlatformTags({ f }: { f: FeatureDefinition }) {
  const app = <span className="inline-flex items-center text-[11px] font-semibold rounded-full px-2 py-px bg-violet-50 text-violet-700">App</span>
  const portal = <span className="inline-flex items-center text-[11px] font-semibold rounded-full px-2 py-px bg-blue-50 text-blue-700">Portal</span>
  return (
    <span className="inline-flex gap-1 ml-1.5 align-[1px]">
      {f.platform === 'mobile' ? app : f.platform === 'both' ? <>{app}{portal}</> : portal}
    </span>
  )
}

export function MigrationCallout({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
      <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>
        {children ?? <>Plans aren't set up on this server yet.</>}{' '}
        Run the registry migration <code className="px-1 rounded bg-amber-100 text-[12px]">{PLAN_MIGRATION}</code> from{' '}
        <Link to={PLAN_MIGRATION_ROUTE} className="font-semibold underline">Database Tools, Registry Migrations</Link>, then reload this page.
      </div>
    </div>
  )
}

export default function Plans() {
  const [data, setData] = useState<PlansResponse | null>(null)
  const [saved, setSaved] = useState<PlanSets>({})
  const [draft, setDraft] = useState<PlanSets>({})
  const [loading, setLoading] = useState(true)
  const [schemaMissing, setSchemaMissing] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [toast, setToast] = useState<{ msg: string; tone: 'ok' | 'error' } | null>(null)

  const notify = useCallback((msg: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ msg, tone })
    window.setTimeout(() => setToast(null), 3500)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const r = await getPlans()
      setData(r)
      setSaved(toSets(r.plan))
      setDraft(toSets(r.plan))
      setSchemaMissing(false)
    } catch (err) {
      if (isPlanSchemaMissing(err)) setSchemaMissing(true)
      else setLoadError(errMsg(err, 'Failed to load plans'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const defs = useMemo(() => data?.definitions ?? [], [data])
  const nameOf = useMemo(() => new Map(defs.map((d) => [d.feature_key, d.feature_name])), [defs])
  const groups = useMemo(() => groupFeatures(defs), [defs])

  const changes: PlanChange[] = useMemo(() => {
    const out: PlanChange[] = []
    for (const t of TIERS) {
      const a = saved[t] ?? new Set<string>()
      const b = draft[t] ?? new Set<string>()
      for (const k of b) if (!a.has(k)) out.push({ tier: t, feature_key: k, included: true })
      for (const k of a) if (!b.has(k)) out.push({ tier: t, feature_key: k, included: false })
    }
    return out
  }, [saved, draft])
  const dirty = changes.length > 0

  // Unsaved-changes guard. The portal uses BrowserRouter (no data router), so
  // react-router's useBlocker isn't available; this covers reload / close tab.
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const summary = useMemo(() => {
    const parts = TIERS.map((t) => {
      const mine = changes.filter((c) => c.tier === t)
      if (!mine.length) return null
      const items = mine.map((c) => `${c.included ? '+' : '−'}${nameOf.get(c.feature_key) ?? c.feature_key}`)
      return `${tierName(t)}: ${items.join(', ')}`
    }).filter(Boolean)
    const affected = [...new Set(changes.map((c) => c.tier))].reduce((n, t) => n + (data?.company_counts?.[t] ?? 0), 0)
    return { text: parts.join('; '), affected }
  }, [changes, nameOf, data])

  const toggle = (tier: Tier, key: string, on: boolean) => {
    setDraft((d) => {
      const next = new Set(d[tier] ?? [])
      if (on) next.add(key)
      else next.delete(key)
      return { ...d, [tier]: next }
    })
  }

  const save = async () => {
    setSaving(true)
    setSaveError('')
    try {
      const r = await savePlans(changes)
      setConfirming(false)
      notify(`Saved. ${r.companies} ${r.companies === 1 ? 'company' : 'companies'} updated.`)
      await load()
    } catch (err) {
      if (isPlanSchemaMissing(err)) { setConfirming(false); setSchemaMissing(true) }
      else setSaveError(errMsg(err, 'Failed to save plans'))
    } finally {
      setSaving(false)
    }
  }

  const tiers = data?.tiers?.length ? data.tiers : TIERS

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h1 className="text-xl font-bold text-slate-900">Plans</h1>
          <div className="ml-auto flex items-center gap-2">
            {dirty && <span className="text-sm text-slate-500">{changes.length} {changes.length === 1 ? 'change' : 'changes'}</span>}
            {dirty && (
              <button
                onClick={() => setDraft(saved)}
                className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
              >
                Discard
              </button>
            )}
            <button
              onClick={() => { setSaveError(''); setConfirming(true) }}
              disabled={!dirty || saving}
              className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save plan changes
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-500 max-w-3xl mb-4">
          What each plan includes. Companies get their plan's features, plus anything you add or remove for them on their own page.
          Changing a plan here updates every company on it, but never undoes a company's own additions, removals, or switches their admin turned off.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-4">
          {[
            ['1 · You, per plan', 'What the plan includes', 'This page.'],
            ['2 · You, per company', 'Add or remove for one company', 'Extras and exceptions survive plan changes.'],
            ['3 · Their admin', 'Turn included features on or off', 'Then roles decide who, for features with a permission.'],
          ].map(([k, b, p], i) => (
            <div key={k} className="bg-white border border-slate-200 rounded-lg px-3.5 py-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{k}</div>
              <b className="block text-sm font-semibold text-slate-900 my-0.5">{b}</b>
              <p className="text-xs text-slate-500">
                {i === 1 ? <>Extras and exceptions survive plan changes. Set them on <Link to="/features" className="text-blue-600 hover:underline">Company Features</Link>.</> : p}
              </p>
            </div>
          ))}
        </div>

        {schemaMissing && <div className="mb-4"><MigrationCallout /></div>}

        {loadError && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" /> {loadError}
            <button onClick={load} className="ml-auto text-xs font-semibold underline">Retry</button>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" /><span className="text-sm">Loading plans…</span>
          </div>
        ) : data && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr>
                  <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-3 py-2.5 bg-slate-50 border-b border-slate-200">Feature</th>
                  {tiers.map((t) => (
                    <th key={t} className="w-[110px] text-center text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-3 py-2.5 bg-slate-50 border-b border-slate-200 whitespace-nowrap">
                      {tierName(t)}
                      <div className="text-xs text-slate-400 font-normal normal-case tracking-normal">
                        {data.company_counts?.[t] ?? 0} {(data.company_counts?.[t] ?? 0) === 1 ? 'company' : 'companies'}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <PlanGroup key={g.key} label={g.label} cols={tiers.length + 1}>
                    {g.items.map((f) => {
                      const core = isCore(f)
                      const parent = f.parent_key ? nameOf.get(f.parent_key) : null
                      return (
                        <tr key={f.feature_key} className="border-b border-slate-200 last:border-b-0">
                          <td className="px-3 py-2.5 align-middle">
                            <b className="font-semibold text-slate-900">{f.feature_name}</b>
                            <PlatformTags f={f} />
                            {core && <span className="ml-1.5 inline-flex text-[11px] font-semibold rounded-full px-2 py-px bg-slate-50 text-slate-600 border border-slate-200 align-[1px]">Always on</span>}
                            <div className="text-xs text-slate-400">
                              {f.description}
                              {parent && <>{f.description ? ' · ' : ''}Part of {parent}</>}
                            </div>
                          </td>
                          {tiers.map((t) => {
                            const on = core || (draft[t]?.has(f.feature_key) ?? false)
                            const changed = !core && on !== (saved[t]?.has(f.feature_key) ?? false)
                            return (
                              <td key={t} className={`px-3 py-2.5 text-center align-middle ${changed ? 'bg-amber-50' : ''}`}>
                                <input
                                  type="checkbox"
                                  className="w-[18px] h-[18px] accent-blue-600 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                                  checked={on}
                                  disabled={core || saving}
                                  onChange={(e) => toggle(t, f.feature_key, e.target.checked)}
                                  aria-label={`${f.feature_name} in ${tierName(t)}`}
                                />
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </PlanGroup>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-labelledby="plans-confirm-title">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200">
              <h2 id="plans-confirm-title" className="text-base font-semibold text-slate-900">Save plan changes?</h2>
              <button onClick={() => setConfirming(false)} className="ml-auto p-1 text-slate-400 hover:text-slate-600" aria-label="Close">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-slate-700">
              <p>{summary.text}.</p>
              <p className="font-medium text-slate-900">
                Affects {summary.affected} {summary.affected === 1 ? 'company' : 'companies'}.
              </p>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-blue-800 text-[13px]">
                <InformationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Companies keep their own additions and removals, and nothing their admin switched off gets switched back on.</span>
              </div>
              {saveError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
                  <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" /> {saveError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setConfirming(false)} className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {saving && <ArrowPathIcon className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div role="status" className={`fixed left-1/2 -translate-x-1/2 bottom-5 z-50 px-4 py-2 rounded-lg text-sm shadow-lg inline-flex items-center gap-2 ${toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>
          {toast.tone === 'ok' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationTriangleIcon className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  )
}

export function PlanGroup({ label, cols, children }: { label: string; cols: number; children: React.ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={cols} className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
          {label}
        </td>
      </tr>
      {children}
    </>
  )
}
